import { lazy, type ComponentType, type LazyExoticComponent } from "react";

// `any`, mint a React saját `lazy` szignatúrájában: a props típusa a betöltött komponensből jön
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

export interface Preloadable {
  /** Elindítja a kód letöltését renderelés nélkül; többszöri hívás is csak egy letöltés */
  preload: () => Promise<void>;
}

export type PreloadableComponent<T extends AnyComponent> = LazyExoticComponent<T> & Preloadable;

/**
 * `React.lazy` előtöltéssel. A komponens kódja külön chunkba kerül, és csak az első
 * rendereléskor (vagy `preload()` hívásra) töltődik le - a nyitóoldalnak így nem kell
 * letöltenie az admin nézeteket vagy a diagram-könyvtárat.
 */
export function lazyWithPreload<T extends AnyComponent>(
  loader: () => Promise<{ default: T }>,
): PreloadableComponent<T> {
  let pending: Promise<{ default: T }> | undefined;
  const load = () => (pending ??= loader());
  const component = lazy(load) as PreloadableComponent<T>;
  component.preload = () => load().then(() => undefined);
  return component;
}

/**
 * Előtöltés a böngésző tétlen idejében, a látható tartalom kirajzolása után - a következő
 * oldal- vagy szekcióváltás így már a gyorsítótárból megy. A visszaadott függvény lemondja.
 */
export function preloadWhenIdle(components: readonly Preloadable[]): () => void {
  const run = () => {
    for (const component of components) {
      // Hálózati hiba itt nem érdekes: a tényleges rendereléskor újrapróbálja
      component.preload().catch(() => undefined);
    }
  };
  if (typeof requestIdleCallback === "function") {
    const id = requestIdleCallback(run);
    return () => cancelIdleCallback(id);
  }
  const id = setTimeout(run, 1500);
  return () => clearTimeout(id);
}
